"use client";

import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useRouter } from "next/navigation";
import { generateKundali, type KundaliResponse } from "@/lib/api/kundali";
import { trackKundaliFormSubmitted, trackKundaliFormSubmitFailed } from "@/lib/analytics-kundali";
import { searchPlaces, type PlaceResult } from "@/lib/api/misc";
import {
  currentPathname,
  finishKundali,
  handleInvalidKundaliForm,
} from "./KundaliFormCard.actions";
import type { useDeliveryCapture } from "./KundaliFormCard.delivery";
import type { CapturedSEOEntry } from "./KundaliFormCard.parts";
import type { KundaliLang } from "./KundaliFormCard.i18n";
import { validateKundaliForm, type KundaliFieldErrors } from "./KundaliFormCard.validation";

export type HandleSubmitDeps = {
  seoEntry: CapturedSEOEntry | null;
  source: string;
  requireBirthTime: boolean;
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  language: KundaliLang;
  selectedPlace: PlaceResult | null;
  delivery: ReturnType<typeof useDeliveryCapture>;
  router: ReturnType<typeof useRouter>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>;
  setResult: Dispatch<SetStateAction<KundaliResponse | null>>;
};

/**
 * Attempt to resolve a typed (but not dropdown-selected) city name to a
 * PlaceResult with coordinates. Returns null on network failure or when the
 * query is too ambiguous to auto-select confidently.
 *
 * Confidence rule: the top result must have a name or display that exactly
 * matches (case-insensitive) what the user typed, OR the user typed ≥4 chars
 * and the top result's name starts with the query (prefix match). A single
 * result for ≥4-char queries is also accepted as unambiguous.
 */
async function resolveTypedPlace(query: string): Promise<PlaceResult | null> {
  const q = query.trim();
  if (!q) return null;
  try {
    const results = await searchPlaces(q, 5);
    if (!results.length) return null;
    const top = results[0];
    const ql = q.toLowerCase();
    const exact = top.display.toLowerCase() === ql || top.name.toLowerCase() === ql;
    const prefix = q.length >= 4 && top.name.toLowerCase().startsWith(ql);
    const unambiguous = q.length >= 4 && results.length === 1;
    if (exact || prefix || unambiguous) return top;
    return null;
  } catch {
    return null;
  }
}

function resolveAnalyticsSource(seoEntry: CapturedSEOEntry | null, source: string) {
  return {
    analyticsSource: seoEntry ? `seo:${seoEntry.config.intent}` : source,
    sourcePath: seoEntry?.sourcePath || currentPathname(),
  };
}

/**
 * The generate → (form-level delivery capture) → render/navigate step, with its
 * own try/catch so a generation failure tracks + degrades without throwing onto
 * the submit handler. Split out of handleSubmit to keep each function within the
 * complexity + line budgets. The delivery capture (ADR-0026) is fire-and-forget.
 */
async function runKundaliGeneration(
  d: HandleSubmitDeps,
  birthDateIso: string,
  analyticsSource: string,
  sourcePath: string | undefined
) {
  let navigated = false;
  try {
    const response = await generateKundali({
      name: d.name,
      birth_date: birthDateIso,
      birth_time: d.birthTime || null,
      birth_place: d.birthPlace,
      language: d.language,
      ...(d.selectedPlace && {
        latitude: d.selectedPlace.latitude,
        longitude: d.selectedPlace.longitude,
        timezone: d.selectedPlace.timezone,
      }),
    });
    void d.delivery.persist(response.id); // form-level delivery capture (soft-fail)
    const ctx = {
      source: d.source,
      language: d.language,
      birthTime: d.birthTime,
      analyticsSource,
      sourcePath,
    };
    navigated = finishKundali(response, ctx, d.router, d.setResult);
  } catch (err) {
    const message = err instanceof Error ? err.message : "Something went wrong";
    d.setError(message);
    trackKundaliFormSubmitFailed({
      language: d.language,
      has_time: !!d.birthTime,
      source: analyticsSource,
      source_path: sourcePath,
      error_message: message,
    });
  } finally {
    if (!navigated) d.setLoading(false);
  }
}

/**
 * The kundali-form submit handler, extracted out of the KundaliFormCard
 * component so that component stays under the function-size budget. Closes over
 * the form state via `d`; behaviour matches the prior inline version.
 */
export function makeHandleSubmit(d: HandleSubmitDeps) {
  return async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const { analyticsSource, sourcePath } = resolveAnalyticsSource(d.seoEntry, d.source);

    // If the user typed a city but never tapped a dropdown suggestion, attempt
    // to resolve the typed text to a confident match before hard-rejecting.
    // Falls back silently to null on network failure so the existing error path
    // still fires when the city is genuinely unresolvable.
    let effectivePlace = d.selectedPlace;
    if (!effectivePlace && d.birthPlace.trim()) {
      effectivePlace = await resolveTypedPlace(d.birthPlace);
    }

    const validation = validateKundaliForm({
      birthDate: d.birthDate,
      birthTime: d.birthTime,
      birthPlace: d.birthPlace,
      language: d.language,
      name: d.name,
      placeSelected: Boolean(effectivePlace),
      requireBirthTime: d.requireBirthTime,
    });
    if (!validation.ok) {
      handleInvalidKundaliForm({
        validation,
        setFieldErrors: d.setFieldErrors,
        setError: d.setError,
        language: d.language,
        birthTime: d.birthTime,
        analyticsSource,
        sourcePath,
      });
      return;
    }
    d.setLoading(true);
    d.setError(null);
    d.setFieldErrors({});
    trackKundaliFormSubmitted({
      language: d.language,
      has_time: !!d.birthTime,
      source: analyticsSource,
      source_path: sourcePath,
    });
    await runKundaliGeneration(
      { ...d, selectedPlace: effectivePlace },
      validation.birthDateIso,
      analyticsSource,
      sourcePath
    );
  };
}
